/* The Profil tab's own boxes — the avatar, the name row, the two feet and the quiet way out.
 * Everything a second tab also draws (the block, the heading, the rows) is
 * `AccountScreen.module.css`'s and is read from there, so no declaration lives in both.
 *
 * Every colour, space, radius and type value is a token; heights and widths are layout. */

/* The mockup's `.who2`: avatar, first name, surname, aligned on the fields' baseline. */
.who {
  display: grid;
  gap: var(--space-14px);
  grid-template-columns: auto 1fr 1fr;
  align-items: end;
}

/* `FormField` carries a 16px bottom margin of its own, so the avatar's box is dropped by the same
 * amount rather than the field's margin being restated here — a declaration written on both sides
 * is settled by chunk order, which differs between the dev server and the build. */
.photoCell {
  position: relative;
  margin-block-end: var(--space-4);
}

.avatar {
  display: grid;
  place-items: center;
  width: var(--sz-account-avatar);
  height: var(--sz-account-avatar);
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  color: var(--text);
  background: var(--bg-sunken);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-circle);
}

/* The disc while the photograph is being prepared and sent. `BrandLoader` sizes itself from `--m`,
 * so the mark is resized through that hook rather than by restating the component's own `width` —
 * and the selector is 0-2-0 so it outranks `.panel`'s 0-1-0 by SPECIFICITY. A single-class override
 * would be settled by chunk order, which differs between the dev server and the build. */
.photoCell .avatarBusy {
  --m: var(--sz-account-avatar-loader);
}

/* The photograph's GEOMETRY; `photoBox` supplies the fit and the neutral ground, which is also the
 * error state — the box exists before the fetch does, so nothing moves when the file lands. */
.photo {
  display: block;
  width: var(--sz-account-avatar);
  height: var(--sz-account-avatar);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-circle);
}

/* The camera, hanging off the disc's corner as the mockup draws it. The `<input type="file">` is
 * inside this label rather than beside it, so the platform's own picker is what opens and the
 * control keeps its place in the tab order with no JavaScript holding it there. */
.camera {
  position: absolute;
  right: var(--space-minus-5px);
  bottom: var(--space-1);
  display: grid;
  place-items: center;
  width: var(--sz-account-camera);
  height: var(--sz-account-camera);
  color: var(--text-2);
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--r-circle);
  cursor: pointer;
}

.camera:hover {
  color: var(--text);
  border-color: var(--text-3);
}

/* The ring goes on the LABEL, because the input inside it is the focused element and is not drawn.
 * Without this the one keyboard path to the photograph has no visible focus at all. */
.camera:has(:focus-visible) {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

.cameraIcon {
  display: block;
  width: var(--sz-account-glyph);
  height: var(--sz-account-glyph);
}

/* The file input itself. Not `display: none`, which takes a control out of the tab order and out of
 * the accessibility tree — the label above is the visible affordance and this is the real control,
 * so it stays focusable and is simply given no area of its own to paint. */
.fileInput {
  position: absolute;
  width: var(--border-hairline);
  height: var(--border-hairline);
  overflow: hidden;
  opacity: 0;
}

/* A second, worded way to remove the photograph. The camera opens the picker; this is the other
 * act, and it is only drawn when there IS a photograph to remove. */
.photoActions {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  margin-top: var(--space-2);
}

.textButton {
  padding: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  text-decoration: underline;

  /* Stylelint's value allowlist refuses the `currentcolor` keyword outright, so the underline's ink
   * travels as a custom property the hover rules re-point. Same outcome, one indirection. */
  --underline-ink: var(--border-2);

  text-decoration-color: var(--underline-ink);
  text-underline-offset: var(--space-3px);
  background: none;
  border: none;
  cursor: pointer;
}

.textButton:hover {
  color: var(--text);

  --underline-ink: var(--text);
}

.textButton:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

/* The mockup's `.foot`: a sentence on the left and the block's one action flush right. */
.foot {
  display: flex;
  gap: var(--space-3);
  align-items: center;
  margin-top: var(--space-3);
}

.footNote {
  flex: 1;
  font-size: var(--fs-micro);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--text-2);
}

/* The two password fields, side by side. */
.pair {
  display: grid;
  gap: var(--space-14px);
  grid-template-columns: 1fr 1fr;
}

/* The four rules, two across, as the mockup lays them out beside the button.
 *
 * They are NOT `features/auth-by-email`'s `PasswordStrength`, and that is a deliberate departure
 * recorded rather than hidden: that component draws a four-segment meter and a tier word above the
 * list, and this canon draws neither — a meter here would be a second reading of the same four
 * facts. What IS shared is everything that could drift: the rule itself is `assessPassword`, and
 * the four words are upstream's own `auth.pw.*`, so the two surfaces cannot come to disagree about
 * what a password must be. */
.rules {
  display: grid;
  flex: 1;
  gap: var(--space-1-5px) var(--space-4);
  grid-template-columns: 1fr 1fr;
  margin: var(--space-0);
  padding: var(--space-0);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
}

.rule {
  display: flex;
  gap: var(--space-7px);
  align-items: center;
  list-style: none;
}

/* The mark is a 7px ring that fills when the rule is met. State is carried by the ROLE as well —
 * each rule is a read-only `checkbox`, which the platform announces as checked or unchecked in the
 * reader's own language — so this is never meaning in colour alone. */
.ruleMark {
  flex: none;
  width: var(--space-7px);
  height: var(--space-7px);
  border: var(--border-thick) solid var(--text-3);
  border-radius: var(--r-circle);
}

.ruleMet {
  color: var(--good-ink);
}

.ruleMet .ruleMark {
  background: var(--good);
  border-color: var(--good);
}

/* The box is `AccountScreen.module.css`'s `.accountButton` / `.accountButtonBold`, worn beside the
 * ink skin. Only the gap between a glyph and the word is this control's own. */
.save {
  gap: var(--space-2);
}

/* The mockup's `.quietout` — „Usuń konto" as a text link at the foot of the tab. A destructive act
 * gets the quietest control on the screen, and it turns danger-coloured only under the pointer.
 *
 * The BOX is `AccountScreen.module.css`'s `.block`, unchanged: the mockup gives this row a slightly
 * tighter top inset than the two blocks above it, and restating `padding` and `border-top` here
 * would put both on two single-class selectors in two files, which is settled by chunk order. Two
 * pixels of inset are not worth a declaration whose outcome differs between the dev server and the
 * build — the argument is `emberButton.module.css`'s, and this repo has already paid for it once. */
.erase:hover {
  color: var(--danger-text-strong);

  --underline-ink: var(--danger-text-strong);
}
